/*
 * Copyright 2026 Sony Corporation
 */

import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import LoginIcon from '@mui/icons-material/Login';
import LogoutIcon from '@mui/icons-material/Logout';
import {
  AppBar,
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Toolbar,
  Typography,
} from '@mui/material';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { useAuth } from '../contexts/auth-context';
import { useSketchfabAuth } from '../hooks/use-sketchfab-auth';
import { SketchfabConnect } from './SketchfabConnect';

export interface AppHeaderProps {
  /** Omitted where the page has nothing worth naming — a scene id is not a name. */
  title?: string;
  /** Rendered between the title and the account controls. */
  children?: React.ReactNode;
  /** A share-link visitor: no way off the page, no account, nothing to do but look. */
  guest?: boolean;
}

const GuestHeader = ({ title }: { title?: string }) => (
  <AppBar position="static" color="default" elevation={0}>
    <Toolbar variant="dense" className="gap-3">
      <Typography variant="subtitle1" color="primary" className="px-2">
        Device Verse
      </Typography>
      {title && (
        <Typography variant="body2" color="text.secondary" noWrap>
          {title}
        </Typography>
      )}
      <Box className="flex-1" />
      <Chip label="View only" size="small" color="info" variant="outlined" />
    </Toolbar>
  </AppBar>
);

const AccountHeader = ({ title, children }: AppHeaderProps) => {
  const { email, signOut, config } = useAuth();
  const sketchfab = useSketchfabAuth();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [connecting, setConnecting] = useState(false);

  return (
    <AppBar position="static" color="default" elevation={0}>
      <Toolbar variant="dense" className="gap-3">
        <Button
          component={Link}
          to="/worlds"
          size="small"
          sx={{ fontSize: (theme) => theme.typography.subtitle1.fontSize }}
        >
          Device Verse
        </Button>
        <Chip label={config.deployment} size="small" variant="outlined" />
        {title && (
          <Typography variant="body2" color="text.secondary" noWrap>
            {title}
          </Typography>
        )}

        <Box className="flex-1" />
        {children}

        <IconButton
          size="small"
          onClick={(event) => setAnchor(event.currentTarget)}
          aria-label="Account"
          aria-haspopup="menu"
          aria-expanded={anchor !== null}
        >
          <AccountCircleIcon color="action" />
        </IconButton>

        <Menu anchorEl={anchor} open={anchor !== null} onClose={() => setAnchor(null)}>
          {/* Who you are signed in as is a label, not a command, so it is not a
              MenuItem: keyboard focus should land on Sign out. */}
          <Box className="px-4 py-1">
            <Typography variant="caption" color="text.secondary" className="block">
              Signed in as
            </Typography>
            <Typography variant="body2">{email ?? 'Unknown account'}</Typography>
          </Box>
          <Divider className="my-1" />

          {/* A second account, not a second identity for this app: Sketchfab
              downloads are made as the user, with the user's own token
              (devplan §5.8). */}
          {sketchfab.session ? (
            <MenuItem
              onClick={() => {
                setAnchor(null);
                sketchfab.disconnect();
              }}
            >
              <ListItemIcon>
                <LinkOffIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary="Disconnect Sketchfab"
                secondary={sketchfab.session.username}
                slotProps={{ secondary: { variant: 'caption' } }}
              />
            </MenuItem>
          ) : (
            <MenuItem
              onClick={() => {
                setAnchor(null);
                setConnecting(true);
              }}
            >
              <ListItemIcon>
                <LoginIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Connect Sketchfab" />
            </MenuItem>
          )}

          <MenuItem
            onClick={() => {
              setAnchor(null);
              signOut();
            }}
          >
            <ListItemIcon>
              <LogoutIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Sign out</ListItemText>
          </MenuItem>
        </Menu>

        <SketchfabConnect open={connecting} onClose={() => setConnecting(false)} />
      </Toolbar>
    </AppBar>
  );
};

export const AppHeader = ({ guest, ...props }: AppHeaderProps) =>
  guest ? <GuestHeader title={props.title} /> : <AccountHeader {...props} />;
